import { Autocomplete, TextField, Chip, CircularProgress } from '@mui/material';
import { useQuery } from 'react-query';
import { getNamedPrices, type NamedPriceDefinition } from '../../../../services/api';

interface NamedPriceSelectProps {
  value: NamedPriceDefinition[] | null;
  onChange: (namedPrices: NamedPriceDefinition[]) => void;
}

/**
 * Picks named prices from the org catalog.
 *
 * The catalog is small and org-wide, so it is fetched once and cached. Only the definition name is
 * shown - the amount on a definition is the organization default, not the store price this operation
 * sets, so surfacing it here would be misleading.
 */
export function NamedPriceSelect({ value, onChange }: NamedPriceSelectProps) {
  const { data, isLoading } = useQuery('namedPrices', getNamedPrices, {
    staleTime: 5 * 60 * 1000,
  });

  const options = data || [];

  const getLabel = (option: NamedPriceDefinition) => option?.name || option?.namedPriceId || 'Unknown';

  return (
    <Autocomplete
      multiple
      options={options}
      loading={isLoading}
      getOptionLabel={getLabel}
      isOptionEqualToValue={(option, val) => option?.namedPriceId === val?.namedPriceId}
      renderOption={(props, option) => (
        <li {...props} key={option.namedPriceId}>
          {getLabel(option)}
        </li>
      )}
      value={Array.isArray(value) ? value : []}
      onChange={(_, newValue) => onChange(newValue as NamedPriceDefinition[])}
      renderTags={(tagValue, getTagProps) =>
        (tagValue as NamedPriceDefinition[]).map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.namedPriceId || `named-price-tag-${index}`}
            size='small'
            label={getLabel(option)}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label='Select Named Price'
          placeholder='Search for named prices...'
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
